import { useMemo } from 'react';

import {
    AllSystemwidePromotionsDocument,
    type IAllSystemwidePromotionsQuery,
} from '@fhs-legacy/frontend/src/generated/sanity-graphql';
import { useSanityQuery } from '@fhs-legacy/frontend/src/state/graphql/hooks/use-sanity-query';

const DOUBLE_POINTS_MULTIPLIER = 2;

type SanityPromotion = IAllSystemwidePromotionsQuery['allSystemwidePromotions'][number];

function isDoublePointsMondayPromo(promo: SanityPromotion): boolean {
    const now = new Date();

    if (promo.testOnly) {
        return false;
    }

    // Must have a point multiplier of exactly 2x
    if (promo.pointsMultiplier !== DOUBLE_POINTS_MULTIPLIER) {
        return false;
    }

    const rules = promo.systemwidePromotion ?? [];

    // Check day-of-week rule: must be Monday only
    for (const rule of rules) {
        if (rule?.__typename === 'DayOfWeekBand') {
            if (
                !rule.monday ||
                rule.tuesday ||
                rule.wednesday ||
                rule.thursday ||
                rule.friday ||
                rule.saturday ||
                rule.sunday
            ) {
                return false;
            }
        }
    }

    // Check date range from between-dates rule or top-level deprecated fields
    let startDate = promo.startDate;
    let endDate = promo.endDate;

    for (const rule of rules) {
        if (rule?.__typename === 'LoyaltyBetweenDates') {
            startDate = rule.startDate ?? startDate;
            endDate = rule.endDate ?? endDate;
        }
    }

    if (startDate && endDate) {
        if (now < new Date(startDate) || now > new Date(endDate)) {
            return false;
        }
    }

    // Must be Monday today (0 = Sunday, 1 = Monday)
    if (now.getDay() !== 1) {
        return false;
    }

    return true;
}

export function useDoublePointsMonday() {
    const { data, loading, error } = useSanityQuery<IAllSystemwidePromotionsQuery>(
        AllSystemwidePromotionsDocument
    );

    const promotion = useMemo(() => {
        if (!data?.allSystemwidePromotions) return null;
        return data.allSystemwidePromotions.find(isDoublePointsMondayPromo) ?? null;
    }, [data]);

    return {
        isDoublePointsMondayOn: !!promotion,
        promotion,
        loading,
        error: error ?? null,
    };
}
